class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.announcementBar = this.querySelector("#wt-announcement__container");
    this.announcementInterval = null;
    this.cloneEl = null;
    this.loopResetTimeout = null;

    this.changeAnnouncement = this.changeAnnouncement.bind(this);
    this.previousAnnouncement = this.previousAnnouncement.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.hideForOneDay = this.hideForOneDay.bind(this);
    this.restartInterval = this.restartInterval.bind(this);
    this.isMobileView = this.isMobileView.bind(this);
  }

  connectedCallback() {
    if (
      !this.announcementBar?.classList.contains(
        "wt-announcement__container--marquee",
      )
    ) {
      this.announcementInterval = setInterval(this.changeAnnouncement, 6000);

      if (this.announcementBar) {
        this.announcementBar.addEventListener(
          "touchstart",
          this.handleTouchStart,
          { passive: true },
        );
        this.announcementBar.addEventListener(
          "touchmove",
          this.handleTouchMove,
          {
            passive: true,
          },
        );
        this.announcementBar.addEventListener("touchend", this.handleTouchEnd, {
          passive: true,
        });
        this.announcementBar.addEventListener(
          "touchstart",
          () => clearInterval(this.announcementInterval),
          { passive: true },
        );
        this.announcementBar.addEventListener(
          "touchend",
          this.restartInterval,
          {
            passive: true,
          },
        );
      }
    }

    if (this.announcementBar)
      this.announcementBar.style.transition = "transform 0.5s ease-in-out";

    this.closeButton = this.querySelector(".wt-announcement__close");
    if (this.closeButton)
      this.closeButton.addEventListener("click", this.hideForOneDay);

    window.addEventListener("resize", this.handleResize.bind(this));
  }

  handleResize() {
    if (window.innerWidth >= 900) {
      // Reset the translateX to its initial state
      if (this.announcementBar) {
        this.removeClone();
        this.announcementBar.style.transform = "translateX(0)";
        this.currentIndex = 0;
      }
    }
  }

  ensureClone() {
    // Infinite-loop trick: append a clone of the first slide after the real
    // ones. Sliding onto it looks identical to sliding onto the real first
    // slide, so we can snap back to position 0 behind the scenes afterwards
    // instead of visibly reversing direction. Only relevant on mobile —
    // removed again in handleResize so it never affects the desktop 3-col
    // layout, which relies on :last-child / :nth-child selectors.
    if (this.cloneEl || !this.announcementBar) return;
    const first = this.announcementBar.children[0];
    if (!first) return;
    this.cloneEl = first.cloneNode(true);
    this.cloneEl.setAttribute("aria-hidden", "true");
    this.announcementBar.appendChild(this.cloneEl);
  }

  removeClone() {
    if (this.cloneEl?.parentNode) {
      this.cloneEl.parentNode.removeChild(this.cloneEl);
    }
    this.cloneEl = null;
  }

  isMobileView() {
    return window.innerWidth < 900;
  }

  restartInterval() {
    clearInterval(this.announcementInterval);
    this.announcementInterval = setInterval(this.changeAnnouncement, 6000);
  }

  hideForOneDay() {
    const oneDayLater = new Date().getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem("wt-announcement-hidden", oneDayLater);
    this.style.display = "none";
  }

  changeAnnouncement() {
    if (!this.isMobileView()) {
      return;
    }

    this.ensureClone();

    const realSlideCount =
      this.announcementBar.children.length - (this.cloneEl ? 1 : 0);

    // Always advance forward — never wrap back with modulo, so the slide
    // never appears to reverse direction.
    this.currentIndex += 1;
    const newPosition = -(this.currentIndex * 100);

    this.announcementBar.style.transform = `translateX(${newPosition}vw)`;

    if (this.currentIndex >= realSlideCount) {
      // We've reached (or, on a fast repeated swipe, overshot) the cloned
      // first slide, which looks identical to the real one. Once the
      // transition finishes, snap back to position 0 with no transition —
      // invisible to the viewer — so the next advance keeps sliding the
      // same direction forever instead of running off the end.
      clearTimeout(this.loopResetTimeout);
      const expectedIndex = this.currentIndex;
      this.loopResetTimeout = setTimeout(() => {
        // If another change fired in the meantime (e.g. a fast swipe),
        // currentIndex has already moved on — don't clobber it back to 0.
        if (this.currentIndex !== expectedIndex) return;
        this.announcementBar.style.transition = "none";
        this.announcementBar.style.transform = "translateX(0)";
        this.currentIndex = 0;
        void this.announcementBar.offsetWidth;
        this.announcementBar.style.transition = "transform 0.5s ease-in-out";
      }, 500);
    }
  }

  previousAnnouncement() {
    const realSlideCount =
      this.announcementBar.children.length - (this.cloneEl ? 1 : 0);

    this.currentIndex =
      (this.currentIndex - 1 + realSlideCount) % realSlideCount;
    const newPosition = -(this.currentIndex * 100);
    if (this.announcementBar)
      this.announcementBar.style.transform = `translateX(${newPosition}vw)`;
  }

  handleTouchStart(e) {
    if (this.isMobileView()) {
      this.touchStartX = e.touches[0].clientX;
    }
  }

  handleTouchMove(e) {
    if (this.isMobileView()) {
      this.touchEndX = e.touches[0].clientX;
    }
  }

  handleTouchEnd() {
    const swipeThreshold = 30;

    if (Math.abs(this.touchEndX - this.touchStartX) > swipeThreshold) {
      if (this.touchEndX < this.touchStartX) {
        this.changeAnnouncement();
      } else {
        this.previousAnnouncement();
      }
    }
  }

  disconnectedCallback() {
    clearInterval(this.announcementInterval);
    clearTimeout(this.loopResetTimeout);
    this.announcementBar.removeEventListener(
      "touchstart",
      this.handleTouchStart,
    );
    this.announcementBar.removeEventListener("touchmove", this.handleTouchMove);
    this.announcementBar.removeEventListener("touchend", this.handleTouchEnd);
    window.removeEventListener("resize", this.handleResize.bind(this));
  }
}

customElements.define("announcement-bar", AnnouncementBar);
